import type { TFunction } from "i18next";

import type { TreatmentOverview } from "../hooks/useTreatmentOverviews";
import { isOtcTreatmentOverview } from "../treatmentOverviewGuards";
import type {
  TreatmentProductLockupDirectionLine,
  TreatmentProductLockupVariant,
} from "../TreatmentProductLockup";

export type SupplementDirectionsLockupProps = {
  variant: Extract<TreatmentProductLockupVariant, "directions">;
  directionLines: TreatmentProductLockupDirectionLine[];
};

type DirectionLinesFactory = (
  t: TFunction<"care">
) => TreatmentProductLockupDirectionLine[];

const metabolicDirectionLines: DirectionLinesFactory = (t) => [
  {
    label: t("detail.supplementDirections.metabolic.morningLabel"),
    text: t("detail.supplementDirections.metabolic.morningText"),
  },
  {
    label: t("detail.supplementDirections.metabolic.eveningLabel"),
    text: t("detail.supplementDirections.metabolic.eveningText"),
  },
];

const vitalityDirectionLines: DirectionLinesFactory = (t) => [
  {
    label: t("detail.supplementDirections.vitality.dailyLabel"),
    text: t("detail.supplementDirections.vitality.dailyText"),
    layout: "stacked",
  },
];

/**
 * Beige info-box dosing copy per supplement, keyed by catalog treatment_code
 * (the identity gql-java puts on the overview). Dosing is product-specific and
 * signed off per product, so a supplement with no entry here shows no info box
 * rather than inheriting another product's directions.
 */
const DIRECTION_LINES_BY_TREATMENT_CODE: Record<string, DirectionLinesFactory> =
  {
    METABOLIC_WOMEN_OTC: metabolicDirectionLines,
    METABOLIC_MEN_OTC: metabolicDirectionLines,
    VITALITY_OTC: vitalityDirectionLines,
  };

/**
 * Beige info-box copy for supplement detail (Figma Med header / states doc).
 * Metabolic: Morning / Evening capsule directions. Vitality: one daily line.
 */
export const getSupplementDirectionsLockupProps = (
  treatment: TreatmentOverview,
  t: TFunction<"care">
): SupplementDirectionsLockupProps | undefined => {
  if (!isOtcTreatmentOverview(treatment)) {
    return undefined;
  }

  const directionLines =
    DIRECTION_LINES_BY_TREATMENT_CODE[treatment.treatmentCode];
  if (!directionLines) {
    return undefined;
  }

  return {
    variant: "directions",
    directionLines: directionLines(t),
  };
};
